/**
 * Single source of truth for per-`ValidationCode` UI metadata.
 *
 * Each candidate row in the preview view carries zero or more issues; the
 * three-bucket tab bar (No-Issues / Warnings / Fix-Issues) is computed by
 * collapsing each issue's `bucket` into the row's worst case. The same
 * meta drives the inline issue list inside the candidate card, the Fix-Issues
 * drill-down panel on the right side of the preview sheet, and the bulk
 * action footer counts.
 *
 * `bucket` is one of `'no_issue' | 'warning' | 'fix'` — the shared three-bucket
 * enum used both per-issue and per-candidate. None of the codes ever lands
 * in `'no_issue'` (an issue without a problem is meaningless), but we keep
 * the wider enum so the filter UI and the candidate-level bucket pivot can
 * share the same palette.
 *
 * Refs:
 * - docs/superpowers/specs/2026-05-07-outreach-05-enrollment-design.md §6.2
 *   (three-bucket classification)
 * - docs/superpowers/specs/2026-05-07-outreach-05-enrollment-design.md §16
 *   (validation engine — issue catalog, severity rules, resolution copy)
 * - All `IconName`s verified against
 *   `frontend/node_modules/@central-icons-react/all/icons/index.d.ts`.
 */

import type { IconName } from "@/components/ui/icon";

import type { PreviewBucket, ValidationCode } from "./types";

export interface IssueMeta {
  code: ValidationCode;
  /** Short title shown inside the issue chip on a candidate card. */
  label: string;
  /** One-sentence explanation of what the recruiter must do to resolve. */
  resolution: string;
  /** Whether this issue is a non-blocking warning or a hard-blocking fix. */
  bucket: PreviewBucket;
  /** Centralicons name for the issue chip icon (verified). */
  icon: IconName;
  /**
   * Tailwind text color for the icon + chip border. Aligned with the
   * three-bucket palette: warnings use amber, fixes use destructive red.
   */
  color: string;
}

export const ISSUE_META: Record<ValidationCode, IssueMeta> = {
  variable_missing_required: {
    code: "variable_missing_required",
    label: "Missing required variable",
    resolution:
      "Edit this step's variable in the per-candidate override or remove the candidate.",
    bucket: "fix",
    icon: "IconVariables",
    color: "text-destructive",
  },
  contact_email_missing: {
    code: "contact_email_missing",
    label: "No deliverable email",
    resolution:
      "Enter an email manually for this candidate or remove them from the cohort.",
    bucket: "fix",
    icon: "IconEmail1",
    color: "text-destructive",
  },
  contact_email_invalid: {
    code: "contact_email_invalid",
    label: "Invalid email address",
    resolution:
      "The on-file email failed validation. Edit it on the candidate or remove them.",
    bucket: "fix",
    icon: "IconEmail1",
    color: "text-destructive",
  },
  linkedin_url_missing: {
    code: "linkedin_url_missing",
    label: "No Social URL on file",
    resolution:
      "Add the Social URL via the profile editor or remove the candidate.",
    bucket: "fix",
    icon: "IconPeopleCircle",
    color: "text-destructive",
  },
  mailbox_disconnected: {
    code: "mailbox_disconnected",
    label: "Mailbox disconnected",
    resolution:
      "Reconnect the sender's mailbox in Settings → Connected Accounts.",
    bucket: "fix",
    icon: "IconMailbox",
    color: "text-destructive",
  },
  linkedin_account_disconnected: {
    code: "linkedin_account_disconnected",
    label: "Social disconnected",
    resolution:
      "Reconnect the sender's Social account in Settings → Connected Accounts.",
    bucket: "fix",
    icon: "IconPeopleCircle",
    color: "text-destructive",
  },
  sequence_not_active: {
    code: "sequence_not_active",
    label: "Sequence not active",
    resolution: "Pick a different sequence or activate this one before enrolling.",
    bucket: "fix",
    icon: "IconCircleBanSign",
    color: "text-destructive",
  },
  step_set_changed: {
    code: "step_set_changed",
    label: "Sequence changed",
    resolution: "Re-open the preview to refresh against the latest step set.",
    bucket: "fix",
    icon: "IconRotate",
    color: "text-destructive",
  },
  duplicate_active_enrollment: {
    code: "duplicate_active_enrollment",
    label: "Already enrolled",
    resolution:
      "Cancel the existing enrollment first, or remove this candidate.",
    bucket: "fix",
    icon: "IconPeopleVersus",
    color: "text-destructive",
  },
  credit_insufficient: {
    code: "credit_insufficient",
    label: "Low signal credits",
    resolution:
      "Top up credits or proceed; we'll skip signal lookups for the affected candidates.",
    bucket: "warning",
    icon: "IconCoin1",
    color: "text-amber-500",
  },
  linkedin_daily_cap_exceeded: {
    code: "linkedin_daily_cap_exceeded",
    label: "Social daily limit reached",
    resolution:
      "Enrolling these candidates exceeds the sender's Social daily limit. Sends will pace across days, so no message is dropped.",
    bucket: "warning",
    icon: "IconPeopleCircle",
    color: "text-amber-500",
  },
  candidate_unsubscribed: {
    code: "candidate_unsubscribed",
    label: "Unsubscribed (opted out)",
    resolution:
      "This person unsubscribed from your organization's outreach. Opt-outs are permanent and legally binding — remove them from the cohort.",
    bucket: "fix",
    icon: "IconCircleBanSign",
    color: "text-destructive",
  },
  candidate_excluded: {
    code: "candidate_excluded",
    label: "On exclusions list",
    resolution:
      "This person is on your organization's people-exclusions list (Settings → Exclusions). Remove them from the cohort, or remove the exclusion if it no longer applies.",
    bucket: "warning",
    icon: "IconExclamationCircle",
    color: "text-amber-500",
  },
};

/**
 * Per-bucket meta for the three-bucket tab bar header (spec §6.2 — "Recipients
 * (4)" + "No Issues / Warnings / Fix Issues" tabs). Drives the tab icon,
 * label, and tab color when a count is non-zero.
 */
export interface BucketMeta {
  value: PreviewBucket;
  label: string;
  icon: IconName;
  color: string;
}

export const BUCKET_META: Record<PreviewBucket, BucketMeta> = {
  no_issue: {
    value: "no_issue",
    label: "No Issues",
    icon: "IconCircleCheck",
    color: "text-success",
  },
  warning: {
    value: "warning",
    label: "Warnings",
    icon: "IconExclamationCircle",
    color: "text-amber-500",
  },
  fix: {
    value: "fix",
    label: "Fix Issues",
    icon: "IconCircleX",
    color: "text-destructive",
  },
};

/**
 * Tab order in the three-bucket tab bar (spec §6.2 — "No Issues" leads,
 * Warnings second, "Fix Issues" last). Default is `no_issue` when the cohort
 * has any issue-free rows; otherwise the first non-empty bucket wins
 * (resolution lives in the preview hook, not here).
 */
export const BUCKET_ORDER: PreviewBucket[] = ["no_issue", "warning", "fix"];
